import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputBase,
  Paper,
  Snackbar,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Badge,
  useTheme,
  useMediaQuery,
  Chip,
} from "@mui/material";
import { getAuthToken } from "../../../utils/getAuthToken";
import { useSubscriptionGuard } from "../../../hooks/useSubscriptionGuard";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import AddIcon from "@mui/icons-material/Add";
import LabelIcon from "@mui/icons-material/Label";
import LabelOffIcon from "@mui/icons-material/LabelOff";
import SettingsIcon from "@mui/icons-material/Settings";
import DeleteIcon from "@mui/icons-material/Delete";
import GroupIcon from "@mui/icons-material/Group";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { ReactComponent as ImportIconBlack } from "../../../Images/Contacts/ImportIconBlack.svg";
import { ReactComponent as ExportIconBlack } from "../../../Images/Contacts/ExportIconBlack.svg";
import AddContact from "./AddContact"; // Import the modal
import { useSelector, useDispatch } from "react-redux";
import {
  bulkDeleteContacts,
  bulkAddContacts,
  setSearchTerm,
  setStatusFilter,
  resetImportProgress,
  setAllCurrentContacts,
} from "../../../redux/Slice/Contacts/contactsSlice"; // Import the bulk delete thunk
import {
  fetchContactGroups,
  addContactsToGroup,
  removeContactsFromGroup,
} from "../../../redux/Slice/Contacts/contactGroupsSlice";
import * as XLSX from "xlsx";
import { fetchContacts } from "../../../redux/Slice/Contacts/contactsSlice.js";
import { fetchUserPermissions } from "../../../redux/Slice/User/userDetailsSlice";
import { clearSelectedContactID } from "../../../redux/Slice/Inbox/inboxAllSlice";
import { clearContactInfo } from "../../../redux/Slice/Inbox/inboxInfoSlice";
import LabelsDialog from "./LabelsDialog";
import LabelsManager from "./LabelsManager";
import ColumnMappingDialog from "./ColumnMappingDialog";
import ContactChannelPicker from "./ContactChannelPicker";
import useContactChannel from "../../../features/whatsapp/hooks/useContactChannel";
import { removeAllLabelsFromContact, fetchLabels, addToLabelFilter, removeFromLabelFilter, clearLabelFilter } from "../../../redux/Slice/Contacts/labelsSlice";
import ClearIcon from "@mui/icons-material/Clear";

const ContactsAction = ({ activeTab = 0, isCompact = false }) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only("xs")); // 0-599px
  const isXl = useMediaQuery(theme.breakpoints.up("xl")); // 1536px+
  
  const [isModalOpen, setIsModalOpen] = useState(false); // Modal open state
  const [searchQuery, setSearchQuery] = useState("");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [labelFilterAnchorEl, setLabelFilterAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('excel'); // Snackbar state
  
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [groupMenuAnchorEl, setGroupMenuAnchorEl] = useState(null);
  const [selectedContactsForGroup, setSelectedContactsForGroup] = useState([]);
  const [labelsDialogOpen, setLabelsDialogOpen] = useState(false);
  const [labelsManagerOpen, setLabelsManagerOpen] = useState(false); // "add" or "remove"
  const [columnMappingOpen, setColumnMappingOpen] = useState(false);
  // CSV import asks once which WhatsApp the contacts are added on (nothing is shown unless the workspace has QR numbers).
  const importChannel = useContactChannel(columnMappingOpen);
  const [fileHeaders, setFileHeaders] = useState([]);
  const [fileData, setFileData] = useState([]);
  const [expandedFilter, setExpandedFilter] = useState(null); // For compact mode: 'status' or 'label' or null
  const fileInputRef = useRef(null);

  const { guard } = useSubscriptionGuard();

  const { selectedContacts, statusFilter, importProgress, contacts, searchTerm, sortField, sortDirection } = useSelector(
    (state) => state.contacts
  );
  const { groups } = useSelector((state) => state.contactGroups);
  const { labelFilter, labels } = useSelector((state) => state.labels);
  const ContactPermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.Contact || {}
  );
  const dispatch = useDispatch();
  const selectedContactID = useSelector((state) => state.inboxAll.selectedContactID);

  // Watch for import progress changes
  useEffect(() => {
    if (importProgress.isImporting) {
      setShowProgressDialog(true);
    } else if (!importProgress.isImporting && showProgressDialog) {
      // Only auto-close on failure, let user manually close on success
      if (importProgress.message.includes("failed")) {
        const timer = setTimeout(() => {
          setShowProgressDialog(false);
          dispatch(resetImportProgress());
        }, 3000);
        
        return () => clearTimeout(timer);
      }
    }
  }, [importProgress.isImporting, importProgress.message, showProgressDialog, dispatch]);

  useEffect(() => {
    dispatch(fetchUserPermissions());
    dispatch(fetchContactGroups());
    dispatch(fetchLabels());
  }, [dispatch]);

  // Handle search input change
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    dispatch(setSearchTerm(value));
  };

  // Handle filter menu
  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  // Handle group management
  const handleGroupMenuClick = (event) => {
    setGroupMenuAnchorEl(event.currentTarget);
    const whatsappIds = selectedContacts.filter((id) => {
      const c = contacts.find((x) => x._id === id);
      return c && (c.platform || "whatsapp") === "whatsapp";
    });
    const skipped = selectedContacts.length - whatsappIds.length;
    if (skipped > 0) {
      setSnackbar({
        open: true,
        message:
          skipped === selectedContacts.length
            ? "Only WhatsApp contacts can be added to or removed from groups."
            : `${skipped} Instagram or Facebook contact(s) omitted; groups support WhatsApp only.`,
        severity: skipped === selectedContacts.length ? "warning" : "info",
      });
    }
    setSelectedContactsForGroup(whatsappIds);
  };

  const handleGroupMenuClose = () => {
    setGroupMenuAnchorEl(null);
    setSelectedContactsForGroup([]);
  };

  const handleAddToGroup = async (groupId) => {
    if (selectedContactsForGroup.length === 0) return;
    if (!await guard([])) return;

    try {
      const result = await dispatch(
        addContactsToGroup({
          groupId,
          contactIds: selectedContactsForGroup,
        })
      ).unwrap();
      
      // Show success message with details
      if (result.totalAdded > 0) {
        setSnackbar({
          open: true,
          message: result.message,
          severity: "success",
        });
        
        // Remove successfully added contacts from selection
        const addedContactIds = result.addedContacts;
        const remainingContacts = selectedContactsForGroup.filter(
          id => !addedContactIds.includes(id)
        );
        
        // Update selected contacts in Redux state
        if (remainingContacts.length === 0) {
          // If all contacts were added, clear selection
          dispatch(setAllCurrentContacts({ 
            ids: selectedContactsForGroup, 
            isRemove: true 
          }));
        } else {
          // Remove only the added contacts from selection
          dispatch(setAllCurrentContacts({ 
            ids: addedContactIds, 
            isRemove: true 
          }));
        }
      }
      
      // Show warning if some contacts were already in group
      if (result.totalAlreadyInGroup > 0) {
        setSnackbar({
          open: true,
          message: `${result.totalAlreadyInGroup} contact(s) were already in this group`,
          severity: "warning",
        });
      }
      
      handleGroupMenuClose();
      // Refresh contacts to update group information
      dispatch(fetchContacts({ page: 1, limit: 25, search: searchTerm, status: statusFilter, labels: labelFilter, sortField, sortDirection }));
    } catch (error) {
      console.error("Failed to add contacts to group:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to add contacts to group",
        severity: "error",
      });
    }
  };

  const handleRemoveFromGroup = async (groupId) => {
    if (selectedContactsForGroup.length === 0) return;
    if (!await guard([])) return;

    try {
      const result = await dispatch(
        removeContactsFromGroup({
          groupId,
          contactIds: selectedContactsForGroup,
        })
      ).unwrap();
      
      // Show success message with details
      if (result.totalRemoved > 0) {
        setSnackbar({
          open: true,
          message: result.message,
          severity: "success",
        });
        
        // Remove successfully removed contacts from selection
        const removedContactIds = result.removedContacts;
        const remainingContacts = selectedContactsForGroup.filter(
          id => !removedContactIds.includes(id)
        );
        
        // Update selected contacts in Redux state
        if (remainingContacts.length === 0) {
          // If all contacts were removed, clear selection
          dispatch(setAllCurrentContacts({ 
            ids: selectedContactsForGroup, 
            isRemove: true 
          }));
        } else {
          // Remove only the removed contacts from selection
          dispatch(setAllCurrentContacts({ 
            ids: removedContactIds, 
            isRemove: true 
          }));
        }
      }
      
      // Show warning if some contacts were not in group
      if (result.totalNotInGroup > 0) {
        setSnackbar({
          open: true,
          message: `${result.totalNotInGroup} contact(s) were not in this group`,
          severity: "warning",
        });
      }
      
      handleGroupMenuClose();
      // Refresh contacts to update group information
      dispatch(fetchContacts({ page: 1, limit: 25, search: searchTerm, status: statusFilter, labels: labelFilter, sortField, sortDirection }));
    } catch (error) {
      console.error("Failed to remove contacts from group:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to remove contacts from group",
        severity: "error",
      });
    }
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterSelect = (status) => {
    dispatch(setStatusFilter(status));
    handleFilterClose();
  };

  const handleAddContact = async () => {
    if (!await guard(['contacts'])) return;
    setIsModalOpen(true);
  };

  const handleImportClick = () => {
    // Must call .click() synchronously within the event handler - the native
    // file picker won't open if we await anything (e.g. the subscription guard)
    // first, since that breaks the browser's user-gesture chain silently.
    fileInputRef.current?.click();
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset file input to allow re-uploading the same file
    event.target.value = "";

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });

      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      // Convert sheet to JSON with headers
      const contacts = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      
      if (contacts.length < 2) {
        setSnackbar({
          open: true,
          message: "File must contain at least a header row and one data row!",
          severity: "warning",
        });
        return;
      }

      // Extract headers (first row)
      const headers = contacts[0];
      
      // Extract data rows (skip header)
      const dataRows = contacts.slice(1);
      
      // Convert data rows to objects for preview
      const previewData = dataRows.map(row => {
        const obj = {};
        headers.forEach((header, index) => {
          obj[header] = row[index];
        });
        return obj;
      });

      // Store file data for column mapping
      setFileHeaders(headers);
      setFileData(previewData);
      setColumnMappingOpen(true);
    };

    reader.readAsArrayBuffer(file);
  };

  const handleColumnMappingConfirm = async (nameColumn, phoneColumn) => {
    setColumnMappingOpen(false);
    if (!await guard(['contacts'])) return;

    try {
      // Process the data with selected columns
      const formattedContacts = fileData
        .map((row) => ({
          name: row[nameColumn],
          phoneNumberID: row[phoneColumn],
        }))
        .filter((contact) => {
          // Filter out empty contacts
          const hasName = contact.name && String(contact.name).trim() !== '';
          const hasPhone = contact.phoneNumberID && String(contact.phoneNumberID).trim() !== '';
          return hasName && hasPhone;
        })
        .map((contact) => ({
          name: String(contact.name).trim(),
          phoneNumberID: String(contact.phoneNumberID).trim().replace(/\D/g, ''), // Normalize phone number by removing all non-digit characters
        }));

      if (formattedContacts.length === 0) {
        setSnackbar({
          open: true,
          message: "No valid contacts found!",
          severity: "warning",
        });
        return;
      }

      // Dispatch bulkAddContacts thunk
      dispatch(bulkAddContacts(importChannel.usingQr ? { contacts: formattedContacts, target: importChannel.target } : formattedContacts))
        .unwrap()
        .then((response) => {
          console.log("Import response:", response);
          
          // Show success notification with contact count
          let successMessage;
          if (response && response.importedCount === 1) {
            successMessage = `${formattedContacts[0].name} imported successfully!`;
          } else if (response && response.importedCount) {
            successMessage = `${response.importedCount} contacts imported successfully!`;
          } else {
            successMessage = `${formattedContacts.length} contacts imported successfully!`;
          }
          
          // Add duplicate information if any
          if (response && response.duplicateCount > 0) {
            successMessage += ` ${response.duplicateCount} duplicate contacts were skipped.`;
          }
          
          setSnackbar({
            open: true,
            message: successMessage,
            severity: "success",
          });
          
          // Refresh contacts list to show newly imported contacts
          dispatch(fetchContacts({ page: 1, limit: 25, search: searchTerm, status: statusFilter, labels: labelFilter, sortField, sortDirection }));
        })
        .catch((error) => {
          console.error("Import error:", error);
          const errMsg =
            typeof error === "string"
              ? error
              : error?.message || "Failed to import contacts.";
          setSnackbar({
            open: true,
            message: errMsg,
            severity: "error",
          });
        });
      
      // Clear file data
      setFileHeaders([]);
      setFileData([]);
    } catch (error) {
      console.error("Column mapping error:", error);
      setSnackbar({
        open: true,
        message: "Error processing file data. Please try again.",
        severity: "error",
      });
    }
  };

  const handleExportContacts = async (format = 'excel') => {
    try {
      console.log("Exporting contacts...");
      
      // Get token from memory (not localStorage)
      const token = await getAuthToken();
      
      // Get userId from Redux state (not localStorage for security)
      const { store } = require('../../../redux/store');
      const user = store.getState()?.auth?.user || null;
      const userId = user?._id || null;

      if (!token || !userId) {
        setSnackbar({
          open: true,
          message: "Authentication required for export",
          severity: "error",
        });
        return;
      }

      // Get server domain with fallback
      const serverDomain = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

      // Call the export API with format parameter
      const response = await fetch(
        `${serverDomain}/api/contacts/export/${userId}?format=${format}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        // Check if response is JSON or HTML
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to export contacts");
        } else {
          // Handle HTML error response (404, 500, etc.)
          const errorText = await response.text();
          console.error("Server returned HTML instead of JSON:", errorText.substring(0, 200));
          throw new Error(`Export failed: ${response.status} ${response.statusText}`);
        }
      }

      const data = await response.json();
      
      setSnackbar({
        open: true,
        message: data.message || `Contacts exported successfully as ${format.toUpperCase()}! Check your email for the file.`,
        severity: "success",
      });
    } catch (error) {
      console.error("Export error:", error);
      setSnackbar({
        open: true,
        message: error.message || "Failed to export contacts",
        severity: "error",
      });
    }
  };

  const handleExportClick = () => {
    setExportDialogOpen(true);
  };

  const handleExportConfirm = () => {
    setExportDialogOpen(false);
    handleExportContacts(selectedFormat);
  };

  const handleExportCancel = () => {
    setExportDialogOpen(false);
  };

  const handleOpenDeleteContactsDialog = async () => {
    if (!await guard([])) return;
    if (selectedContacts.length === 0) {
      setSnackbar({
        open: true,
        message: "No contacts selected for deletion.",
        severity: "warning",
      });
      return;
    }
    setDeleteDialogOpen(true);
  };

  const handleDeleteContactsDialogClose = () => {
    if (!deleteInProgress) setDeleteDialogOpen(false);
  };

  const bulkDeleteConfirmMessage =
    selectedContacts.length === 0
      ? ""
      : selectedContacts.length === 1
        ? `Are you sure you want to permanently delete ${
            contacts.find((x) => x._id === selectedContacts[0])?.name || "this contact"
          }? This cannot be undone.`
        : `Are you sure you want to permanently delete ${selectedContacts.length} contacts? This cannot be undone.`;

  // Handle bulk delete (after confirmation)
  const handleConfirmDeleteSelectedContacts = () => {
    if (selectedContacts.length === 0) {
      setDeleteDialogOpen(false);
      return;
    }

    const contactNames = selectedContacts.map((contactId) => {
      const contact = contacts.find((c) => c._id === contactId);
      return contact ? contact.name : "Unknown";
    });

    setDeleteInProgress(true);
    dispatch(bulkDeleteContacts(selectedContacts)).then((action) => {
      setDeleteInProgress(false);
      setDeleteDialogOpen(false);
      if (action.type.endsWith("fulfilled")) {
        const deletedIds = action.payload.deletedIds || [];
        if (deletedIds.includes(selectedContactID)) {
          dispatch(clearSelectedContactID());
          dispatch(clearContactInfo());
        }

        let successMessage;
        if (deletedIds.length === 1) {
          successMessage = `${contactNames[0]} deleted successfully!`;
        } else {
          successMessage = `${deletedIds.length} contacts deleted successfully!`;
        }

        setSnackbar({
          open: true,
          message: successMessage,
          severity: "success",
        });
      } else if (action.type.endsWith("rejected")) {
        setSnackbar({
          open: true,
          message: action.payload || "Failed to delete contacts",
          severity: "error",
        });
      }
    });
  };

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Labels management functions
  const handleAddLabels = async () => {
    if (!await guard([])) return;
    if (selectedContacts.length === 0) {
      setSnackbar({
        open: true,
        message: "No contacts selected for adding labels.",
        severity: "warning",
      });
      return;
    }
    setLabelsDialogOpen(true);
  };

  const handleRemoveLabels = async () => {
    if (!await guard([])) return;
    if (selectedContacts.length === 0) {
      setSnackbar({
        open: true,
        message: "No contacts selected for removing labels.",
        severity: "warning",
      });
      return;
    }

    // Get contacts that have labels
    const contactsWithLabels = contacts.filter(contact => 
      selectedContacts.includes(contact._id) && 
      Array.isArray(contact.labels) && 
      contact.labels.length > 0
    );

    if (contactsWithLabels.length === 0) {
      setSnackbar({
        open: true,
        message: "Please select only contacts that have labels to remove.",
        severity: "info",
      });
      return;
    }

    try {
      // Use the new removeAllLabelsFromContact action for each contact
      const promises = contactsWithLabels.map(contact =>
        dispatch(
          removeAllLabelsFromContact({
            contactId: contact._id,
          })
        ).unwrap()
      );

      await Promise.all(promises);

      // Show success message with contact name(s)
      let successMessage;
      
      if (contactsWithLabels.length === 1) {
        const contact = contactsWithLabels[0];
        const labelCount = contact.labels.length;
        if (labelCount === 1) {
          successMessage = `Label '${contact.labels[0]}' removed from ${contact.name} successfully!`;
        } else {
          successMessage = `All ${labelCount} labels removed from ${contact.name} successfully!`;
        }
      } else {
        successMessage = `All labels removed from ${contactsWithLabels.length} contacts successfully!`;
      }

      setSnackbar({
        open: true,
        message: successMessage,
        severity: "success",
      });

      // Refresh contacts to show updated labels
      dispatch(fetchContacts({ page: 1, limit: 25, search: searchTerm, status: statusFilter, labels: labelFilter, sortField, sortDirection }));
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.error || "Failed to remove labels",
        severity: "error",
      });
    }
  };

  const handleLabelsDialogSuccess = () => {
    // Refresh contacts to show updated labels
    dispatch(fetchContacts({ page: 1, limit: 25, search: searchTerm, status: statusFilter, labels: labelFilter, sortField, sortDirection }));
  };

  const handleManageLabels = async () => {
    if (!await guard([])) return;
    setLabelsManagerOpen(true);
  };

  // Compact mode for xs screens - icon-only horizontal bar
  if (isCompact) {
    const handleFilterMenuClick = (event) => {
      setFilterAnchorEl(event.currentTarget);
      setExpandedFilter(null);
    };

    const handleFilterMenuClose = () => {
      setFilterAnchorEl(null);
      setExpandedFilter(null);
    };

    const handleFilterHeadingClick = (filterType) => {
      setExpandedFilter(expandedFilter === filterType ? null : filterType);
    };

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          maxWidth: "100%",
          padding: "4px 0px", // Remove horizontal padding since parent has it
          gap: 0.2, // Decreased gap between icons
          alignItems: "center",
          backgroundColor: (theme) => theme.palette.background.paper,
          overflowX: "auto",
          overflowY: "hidden",
          boxSizing: "border-box",
          "&::-webkit-scrollbar": {
            height: "4px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1E1E1E" : "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: (theme) => theme.palette.mode === "dark" ? "#888" : "#888",
            borderRadius: "2px",
          },
        }}
      >
        {/* Search Bar */}
        <Box
          sx={{
            position: "relative",
            flex: 1,
            minWidth: "80px", // Decreased search bar size
            maxWidth: "140px", // Added max width
            border: (theme) => `1px solid ${theme.palette.success.main}`,
            borderRadius: "3px",
            display: "flex",
            alignItems: "center",
            height: "24px",
            backgroundColor: "transparent",
            "&:focus-within": {
              borderColor: (theme) => theme.palette.success.main,
            },
          }}
        >
          <SearchIcon
            sx={{
              position: "absolute",
              left: "4px",
              fontSize: "12px",
              color: (theme) => theme.palette.success.main,
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
          <InputBase
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearchChange}
            sx={{
              flex: 1,
              paddingLeft: "20px",
              paddingRight: "4px",
              height: "100%",
              fontSize: "9px",
              color: (theme) => theme.palette.success.main,
              "& input::placeholder": {
                fontSize: "9px",
                color: (theme) => theme.palette.success.main,
                opacity: 1,
              },
            }}
          />
        </Box>

        {/* Filter Icon */}
        <Tooltip title="Filters">
          <IconButton
            onClick={handleFilterMenuClick}
            sx={{
              padding: "3px",
              minWidth: "28px",
              width: "28px",
              height: "28px",
              "& .MuiSvgIcon-root": {
                fontSize: "16px",
                color: (theme) => theme.palette.success.main,
              },
            }}
          >
            <FilterAltOutlinedIcon />
          </IconButton>
        </Tooltip>
        
        {/* Filter Menu with expandable sections */}
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={handleFilterMenuClose}
          PaperProps={{
            sx: {
              minWidth: 180,
              maxWidth: 220,
              borderRadius: "6px",
              mt: 0.5,
            },
          }}
        >
          {/* Status Filter Heading */}
          <MenuItem 
            onClick={() => handleFilterHeadingClick('status')}
            sx={{
              minHeight: "32px",
              padding: "4px 8px",
            }}
          >
            <ListItemText 
              primary="Status Filter" 
              primaryTypographyProps={{
                fontSize: "10px",
                fontWeight: 500,
              }}
            />
            {expandedFilter === 'status' ? <ArrowUpwardIcon sx={{ fontSize: "12px", ml: 1 }} /> : <ArrowDownwardIcon sx={{ fontSize: "12px", ml: 1 }} />}
          </MenuItem>
          
          {/* Status Filter Options - Show when expanded */}
          {expandedFilter === 'status' && (
            <>
              <MenuItem 
                onClick={() => { handleFilterSelect("all"); handleFilterMenuClose(); }} 
                selected={statusFilter === "all"}
                sx={{ minHeight: "28px", padding: "2px 16px" }}
              >
                <ListItemText 
                  primary="All Contacts"
                  primaryTypographyProps={{ fontSize: "9px" }}
                />
              </MenuItem>
              <MenuItem
                onClick={() => { handleFilterSelect("closed"); handleFilterMenuClose(); }}
                selected={statusFilter === "closed"}
                sx={{ minHeight: "28px", padding: "2px 16px" }}
              >
                <ListItemText
                  primary="Closed"
                  primaryTypographyProps={{ fontSize: "9px" }}
                />
              </MenuItem>
              <MenuItem 
                onClick={() => { handleFilterSelect("assigned"); handleFilterMenuClose(); }} 
                selected={statusFilter === "assigned"}
                sx={{ minHeight: "28px", padding: "2px 16px" }}
              >
                <ListItemText 
                  primary="Assigned"
                  primaryTypographyProps={{ fontSize: "9px" }}
                />
              </MenuItem>
              <MenuItem 
                onClick={() => { handleFilterSelect("unassigned"); handleFilterMenuClose(); }} 
                selected={statusFilter === "unassigned"}
                sx={{ minHeight: "28px", padding: "2px 16px" }}
              >
                <ListItemText 
                  primary="Unassigned"
                  primaryTypographyProps={{ fontSize: "9px" }}
                />
              </MenuItem>
            </>
          )}
          
          <Divider sx={{ my: 0.5 }} />
          
          {/* Label Filter Heading */}
          <MenuItem 
            onClick={() => handleFilterHeadingClick('label')}
            sx={{
              minHeight: "32px",
              padding: "4px 8px",
            }}
          >
            <ListItemText 
              primary="Label Filter" 
              primaryTypographyProps={{
                fontSize: "10px",
                fontWeight: 500,
              }}
            />
            {expandedFilter === 'label' ? <ArrowUpwardIcon sx={{ fontSize: "12px", ml: 1 }} /> : <ArrowDownwardIcon sx={{ fontSize: "12px", ml: 1 }} />}
          </MenuItem>
          
          {/* Label Filter Options - Show when expanded */}
          {expandedFilter === 'label' && (
            <>
              {labels
                .filter((label) => !labelFilter.includes(label))
                .map((label) => (
                  <MenuItem
                    key={label}
                    onClick={() => {
                      dispatch(addToLabelFilter(label));
                      handleFilterMenuClose();
                    }}
                    sx={{ minHeight: "28px", padding: "2px 16px" }}
                  >
                    <ListItemText 
                      primary={label}
                      primaryTypographyProps={{ fontSize: "9px" }}
                    />
                  </MenuItem>
                ))}
              {labels.filter((label) => !labelFilter.includes(label)).length === 0 && (
                <MenuItem disabled sx={{ minHeight: "28px", padding: "2px 16px" }}>
                  <ListItemText 
                    primary="No labels available"
                    primaryTypographyProps={{ fontSize: "9px" }}
                  />
                </MenuItem>
              )}
              {labelFilter.length > 0 && (
                <>
                  <Divider sx={{ my: 0.5 }} />
                  <MenuItem
                    onClick={() => {
                      dispatch(clearLabelFilter());
                      handleFilterMenuClose();
                    }}
                    sx={{ minHeight: "28px", padding: "2px 16px" }}
                  >
                    <ListItemText 
                      primary="Clear Label Filters"
                      primaryTypographyProps={{ fontSize: "9px" }}
                    />
                  </MenuItem>
                </>
              )}
            </>
          )}
        </Menu>

        {/* Action Icons Bar - All in one row */}
        <Box sx={{ display: "flex", gap: 0.2, alignItems: "center", flexShrink: 0 }}>
          {/* Import */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx, .csv"
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />
          <Tooltip title="Import">
            <span>
              <IconButton
                onClick={handleImportClick}
                disabled={!ContactPermissions.Add || importProgress.isImporting}
                sx={{
                  padding: "3px",
                  minWidth: "28px",
                  width: "28px",
                  height: "28px",
                  "& .MuiSvgIcon-root": {
                    fontSize: "16px",
                  },
                }}
              >
                <Box
                  component={ExportIconBlack}
                  sx={{
                    width: "16px",
                    height: "16px",
                    color: (theme) => theme.palette.success.main,
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>

          {/* Export */}
          <Tooltip title="Export">
            <span>
              <IconButton
                onClick={handleExportClick}
                disabled={!ContactPermissions.Export}
                sx={{
                  padding: "3px",
                  minWidth: "28px",
                  width: "28px",
                  height: "28px",
                  "& .MuiSvgIcon-root": {
                    fontSize: "16px",
                  },
                }}
              >
                <Box
                  component={ImportIconBlack}
                  sx={{
                    width: "16px",
                    height: "16px",
                    color: (theme) => theme.palette.success.main,
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>

          {/* Add Label */}
          <Tooltip title="Add Label">
            <span>
              <IconButton
                onClick={handleAddLabels}
                disabled={selectedContacts.length === 0}
                sx={{
                  padding: "3px",
                  minWidth: "28px",
                  width: "28px",
                  height: "28px",
                  "& .MuiSvgIcon-root": {
                    fontSize: "16px",
                    color: (theme) => theme.palette.success.main,
                  },
                }}
              >
                <LabelIcon />
              </IconButton>
            </span>
          </Tooltip>

          {/* Remove Label */}
          <Tooltip title="Remove Label">
            <span>
              <IconButton
                onClick={handleRemoveLabels}
                disabled={selectedContacts.length === 0}
                sx={{
                  padding: "3px",
                  minWidth: "28px",
                  width: "28px",
                  height: "28px",
                  "& .MuiSvgIcon-root": {
                    fontSize: "16px",
                    color: (theme) => theme.palette.success.main,
                  },
                }}
              >
                <LabelOffIcon />
              </IconButton>
            </span>
          </Tooltip>

          {/* Manage Labels */}
          <Tooltip title="Manage Labels">
            <IconButton
              onClick={handleManageLabels}
              sx={{
                padding: "3px",
                minWidth: "28px",
                width: "28px",
                height: "28px",
                "& .MuiSvgIcon-root": {
                  fontSize: "16px",
                  color: (theme) => theme.palette.success.main,
                },
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>

          {/* Add to Group */}
          <Tooltip title="Add to Group">
            <span>
              <IconButton
                onClick={handleGroupMenuClick}
                disabled={selectedContacts.length === 0}
                sx={{
                  padding: "3px",
                  minWidth: "28px",
                  width: "28px",
                  height: "28px",
                  "& .MuiSvgIcon-root": {
                    fontSize: "16px",
                    color: (theme) => theme.palette.success.main,
                  },
                }}
              >
                <Badge 
                  badgeContent={selectedContacts.length} 
                  color="primary"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "7px",
                      height: "12px",
                      minWidth: "12px",
                    },
                  }}
                >
                  <GroupIcon />
                </Badge>
              </IconButton>
            </span>
          </Tooltip>

          {/* Delete */}
          <Tooltip title="Delete">
            <span>
              <IconButton
                onClick={handleOpenDeleteContactsDialog}
                disabled={!ContactPermissions.Delete}
                sx={{
                  padding: "3px",
                  minWidth: "28px",
                  width: "28px",
                  height: "28px",
                  "& .MuiSvgIcon-root": {
                    fontSize: "16px",
                    color: (theme) => theme.palette.success.main,
                  },
                }}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {/* All dialogs and menus remain the same */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        <LabelsDialog
          open={labelsDialogOpen}
          onClose={() => setLabelsDialogOpen(false)}
          contactIds={selectedContacts}
          isBulkOperation={true}
          onSuccess={handleLabelsDialogSuccess}
        />

        <Dialog
          open={showProgressDialog}
          onClose={() => {}}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
            <Typography variant="h6">Importing Contacts</Typography>
          </DialogTitle>
          <DialogContent sx={{ pb: 3 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {importProgress.message}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {Math.floor(importProgress.current)} of {importProgress.total} contacts processed
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold" }}>
                  {importProgress.total > 0 ? Math.round((Math.floor(importProgress.current) / importProgress.total) * 100) : 0}%
                </Typography>
              </Box>
            </Box>
            <LinearProgress
              variant="determinate"
              value={importProgress.total > 0 ? (Math.floor(importProgress.current) / importProgress.total) * 100 : 0}
              sx={{
                height: 8,
                borderRadius: 4,
              }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={() => {
                setShowProgressDialog(false);
                dispatch(resetImportProgress());
              }}
              variant="outlined"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Menu
          anchorEl={groupMenuAnchorEl}
          open={Boolean(groupMenuAnchorEl)}
          onClose={handleGroupMenuClose}
          PaperProps={{
            sx: {
              minWidth: 250,
              borderRadius: "8px",
            },
          }}
        >
          <MenuItem disabled>
            <ListItemText 
              primary={`${selectedContactsForGroup.length} contact${selectedContactsForGroup.length !== 1 ? 's' : ''} selected`}
              secondary="Select an action"
            />
          </MenuItem>
          <Divider />
          {groups.length > 0 ? [
            <MenuItem key="add-to-group-header" disabled>
              <ListItemText primary="Add to Group" />
            </MenuItem>,
            ...groups.map((group) => (
              <MenuItem
                key={group._id}
                onClick={() => handleAddToGroup(group._id)}
              >
                <ListItemIcon>
                  <GroupIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary={group.name}
                  secondary={`${group.contactCount || 0} contacts`}
                />
              </MenuItem>
            )),
            <Divider key="divider-1" />,
            <MenuItem key="remove-from-group-header" disabled>
              <ListItemText primary="Remove from Group" />
            </MenuItem>,
            ...groups.map((group) => (
              <MenuItem
                key={`remove-${group._id}`}
                onClick={() => handleRemoveFromGroup(group._id)}
              >
                <ListItemIcon>
                  <GroupIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary={group.name}
                  secondary={`${group.contactCount || 0} contacts`}
                />
              </MenuItem>
            ))
          ] : (
            <MenuItem disabled>
              <ListItemText 
                primary="No groups available"
                secondary="Create contact groups first"
              />
            </MenuItem>
          )}
        </Menu>

        <LabelsManager
          open={labelsManagerOpen}
          onClose={() => setLabelsManagerOpen(false)}
          onLabelDeleted={handleLabelsDialogSuccess}
        />

        <Dialog
          open={exportDialogOpen}
          onClose={handleExportCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
            <Typography variant="h6">Select Export Format</Typography>
          </DialogTitle>
          <DialogContent sx={{ pb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
              Choose the format for exporting your contacts:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant={selectedFormat === 'excel' ? 'contained' : 'outlined'}
                onClick={() => setSelectedFormat('excel')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5, px: 2 }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Excel (.xlsx)
                  </Typography>
                  <Typography variant="body2" sx={{ color: selectedFormat === 'excel' ? 'white' : 'text.secondary' }}>
                    Recommended for complex data with formatting
                  </Typography>
                </Box>
              </Button>
              <Button
                variant={selectedFormat === 'csv' ? 'contained' : 'outlined'}
                onClick={() => setSelectedFormat('csv')}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5, px: 2 }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    CSV (.csv)
                  </Typography>
                  <Typography variant="body2" sx={{ color: selectedFormat === 'csv' ? 'white' : 'text.secondary' }}>
                    Compatible with most applications and spreadsheets
                  </Typography>
                </Box>
              </Button>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleExportCancel} color="secondary" variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleExportConfirm} variant="contained" color="primary" sx={{ textTransform: 'none' }}>
              Export {selectedFormat.toUpperCase()}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteContactsDialogClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
            <Typography variant="h6">Delete contacts</Typography>
          </DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            <Typography variant="body1" sx={{ textAlign: "center" }}>
              {bulkDeleteConfirmMessage}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleDeleteContactsDialogClose}
              color="secondary"
              variant="outlined"
              disabled={deleteInProgress}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDeleteSelectedContacts}
              variant="contained"
              color="error"
              disabled={deleteInProgress}
              sx={{ textTransform: "none" }}
            >
              {deleteInProgress ? "Deleting…" : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        <ColumnMappingDialog
          open={columnMappingOpen}
          onClose={() => {
            setColumnMappingOpen(false);
            setFileHeaders([]);
            setFileData([]);
          }}
          onConfirm={handleColumnMappingConfirm}
          headers={fileHeaders}
          previewData={fileData}
          extraContent={<ContactChannelPicker channelState={importChannel} />}
          confirmDisabled={!importChannel.ready}
        />
      </Box>
    );
  }

  // Regular mode for other screen sizes
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        maxWidth: "100%",
        bgcolor: (theme) => theme.palette.background.paper,
        overflowY: { xs: "auto", sm: "auto", md: "auto", lg: "auto", xl: "hidden" },
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* Search and Filter */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: "100%",
          padding: { xs: "2px", sm: 0.4, md: 0.5, lg: 0.75, xl: 2 },
          paddingTop: { xs: 6, sm: 5, md: 3, lg: 3, xl: 2 },
          flexShrink: 0,
          gap: { xs: 0.3, sm: 0.4, md: 0.5, lg: 0.6, xl: 0 },
          boxSizing: "border-box",
          marginTop: { xs: 0.5, sm: 0.5, md: 0.75, lg: 0.75, xl: 0 },
          overflow: "hidden",
        }}
      >
        {/* Search Input - Separate row for xs, sm, md, lg */}
        {!isXl ? (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: "100%",
              border: (theme) => `1px solid ${theme.palette.primary.main}`,
              borderRadius: { xs: "3px", sm: "4px", md: "5px", lg: "6px" },
              display: "flex",
              alignItems: "center",
              height: { xs: "20px", sm: "22px", md: "26px", lg: "28px" },
              backgroundColor: "transparent",
              boxSizing: "border-box",
              overflow: "hidden",
              flexShrink: 0,
              margin: 0,
              "&:focus-within": {
                borderColor: (theme) => theme.palette.primary.main,
              },
            }}
          >
            {!isXs && (
              <SearchIcon
                sx={{
                  position: "absolute",
                  left: { xs: "2px", sm: "3px", md: "4px", lg: "5px" },
                  fontSize: { xs: "9px", sm: "10px", md: "12px", lg: "13px" },
                  color: (theme) => theme.palette.primary.main,
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
            )}
            <InputBase
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{
                flex: 1,
                minWidth: 0,
                width: 0,
                paddingLeft: { xs: "2px", sm: "17px", md: "22px", lg: "25px" },
                paddingRight: { xs: "2px", sm: "3px", md: "5px", lg: "6px" },
                paddingTop: { xs: "0px", sm: "0px", md: "0px", lg: "0px" },
                paddingBottom: { xs: "0px", sm: "0px", md: "0px", lg: "0px" },
                height: "100%",
                fontSize: { xs: "8px", sm: "8px", md: "9px", lg: "10px" },
                color: (theme) => theme.palette.primary.main,
                boxSizing: "border-box",
                "& input": {
                  height: "100%",
                  padding: 0,
                  width: "100%",
                  boxSizing: "border-box",
                  color: (theme) => theme.palette.primary.main,
                },
                "& input::placeholder": {
                  fontSize: { xs: "8px", sm: "8px", md: "9px", lg: "10px" },
                  color: (theme) => theme.palette.primary.main,
                  opacity: 1,
                },
              }}
            />
          </Box>
        ) : (
          <Paper
            component="form"
            sx={{
              display: "flex",
              alignItems: "center",
              borderRadius: "20px",
              border: (theme) => `1px solid ${theme.palette.divider}`,
              boxShadow: "none",
              backgroundColor: (theme) => theme.palette.background.paper,
              width: "100%",
              minHeight: "48px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, padding: "0" }}>
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={handleSearchChange}
                style={{
                  border: "none",
                  outline: "none",
                  background: "transparent",
                  width: "100%",
                  fontSize: "0.875rem",
                  color: "inherit",
                  padding: "0",
                  paddingLeft: "28px",
                }}
              />
              <IconButton
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                sx={{
                  color: (theme) => theme.palette.text.secondary,
                  padding: "8px",
                  "& .MuiSvgIcon-root": {
                    fontSize: "24px",
                  },
                  "&:hover": {
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.08)"
                        : "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <SearchIcon />
              </IconButton>
            </Box>
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: (theme) => theme.palette.divider,
              }}
            />
            <IconButton
              color="default"
              sx={{
                p: "10px",
                color: (theme) => theme.palette.text.primary,
                "& .MuiSvgIcon-root": {
                  fontSize: "24px",
                },
              }}
              onClick={handleFilterClick}
            >
              <FilterAltOutlinedIcon />
            </IconButton>
          </Paper>
        )}
        
        {/* Filter Button - Separate row for xs, sm, md, lg */}
        {!isXl && (
          <Button
            variant="outlined"
            startIcon={!isXs ? <FilterAltOutlinedIcon sx={{ fontSize: { xs: "9px", sm: "10px", md: "12px", lg: "13px" } }} /> : null}
            onClick={handleFilterClick}
            sx={{
              textTransform: "none",
              fontSize: { xs: "8px", sm: "8px", md: "9px", lg: "10px" },
              borderRadius: { xs: "3px", sm: "4px", md: "5px", lg: "6px" },
              padding: { xs: "1px 2px", sm: "2px 5px", md: "3px 7px", lg: "4px 9px" },
              height: { xs: "20px", sm: "22px", md: "26px", lg: "28px" },
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              boxSizing: "border-box",
              flexShrink: 0,
              margin: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              "& .MuiButton-startIcon": {
                marginRight: { xs: "0px", sm: "2px", md: "3px", lg: "3px" },
                display: { xs: "none", sm: "flex" },
              },
            }}
          >
            Filter by Status
          </Button>
        )}

        {/* Filter by Labels Button - Separate row for xs, sm, md, lg */}
        {!isXl && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 0.3, sm: 0.3, md: 0.4, lg: 0.4 }, width: "100%" }}>
            <Button
              variant="outlined"
              startIcon={!isXs ? <FilterAltOutlinedIcon sx={{ fontSize: { xs: "9px", sm: "10px", md: "12px", lg: "13px" } }} /> : null}
              onClick={(e) => setLabelFilterAnchorEl(e.currentTarget)}
              sx={{
                textTransform: "none",
                fontSize: { xs: "8px", sm: "8px", md: "9px", lg: "10px" },
                borderRadius: { xs: "3px", sm: "4px", md: "5px", lg: "6px" },
                padding: { xs: "1px 2px", sm: "2px 5px", md: "3px 7px", lg: "4px 9px" },
                height: { xs: "20px", sm: "22px", md: "26px", lg: "28px" },
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                boxSizing: "border-box",
                flexShrink: 0,
                margin: 0,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                "& .MuiButton-startIcon": {
                  marginRight: { xs: "0px", sm: "2px", md: "3px", lg: "3px" },
                  display: { xs: "none", sm: "flex" },
                },
              }}
            >
              Filter by Labels
            </Button>
            
            {/* Selected Label Chips */}
            {labelFilter.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 0.15, sm: 0.15, md: 0.2, lg: 0.2 }, width: "100%" }}>
                {labelFilter.map((label) => (
                  <Chip
                    key={label}
                    label={label}
                    size="small"
                    onDelete={() => dispatch(removeFromLabelFilter(label))}
                    sx={{
                      fontSize: { xs: "5px", sm: "6px", md: "7px", lg: "8px" },
                      height: { xs: "14px", sm: "16px", md: "18px", lg: "20px" },
                      "& .MuiChip-deleteIcon": {
                        fontSize: { xs: "7px", sm: "8px", md: "10px", lg: "12px" },
                      },
                    }}
                  />
                ))}
                {labelFilter.length > 0 && (
                  <IconButton
                    size="small"
                    onClick={() => dispatch(clearLabelFilter())}
                    sx={{
                      padding: { xs: "0px", sm: "1px", md: "2px", lg: "3px" },
                      "& .MuiSvgIcon-root": {
                        fontSize: { xs: "9px", sm: "10px", md: "12px", lg: "14px" },
                      },
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        PaperProps={{
          sx: {
            minWidth: 200,
            borderRadius: "8px",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 2px 4px rgba(0,0,0,0.3)"
                : "0 2px 4px rgba(0,0,0,0.1)",
            backgroundColor: (theme) => theme.palette.background.paper,
          },
        }}
      >
        <MenuItem
          onClick={() => handleFilterSelect("all")}
          selected={statusFilter === "all"}
        >
          <ListItemText primary="All Contacts" />
        </MenuItem>
        <MenuItem
          onClick={() => handleFilterSelect("closed")}
          selected={statusFilter === "closed"}
        >
          <ListItemText primary="Closed" />
        </MenuItem>
        <MenuItem
          onClick={() => handleFilterSelect("assigned")}
          selected={statusFilter === "assigned"}
        >
          <ListItemText primary="Assigned" />
        </MenuItem>
        <MenuItem
          onClick={() => handleFilterSelect("unassigned")}
          selected={statusFilter === "unassigned"}
        >
          <ListItemText primary="Unassigned" />
        </MenuItem>
      </Menu>

      {/* Label Filter Menu */}
      <Menu
        anchorEl={labelFilterAnchorEl}
        open={Boolean(labelFilterAnchorEl)}
        onClose={() => setLabelFilterAnchorEl(null)}
        PaperProps={{
          sx: {
            minWidth: 200,
            borderRadius: "8px",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 2px 4px rgba(0,0,0,0.3)"
                : "0 2px 4px rgba(0,0,0,0.1)",
            backgroundColor: (theme) => theme.palette.background.paper,
          },
        }}
      >
        {labels
          .filter((label) => !labelFilter.includes(label))
          .map((label) => (
            <MenuItem
              key={label}
              onClick={() => {
                dispatch(addToLabelFilter(label));
                setLabelFilterAnchorEl(null);
              }}
            >
              <ListItemText primary={label} />
            </MenuItem>
          ))}
        {labels.filter((label) => !labelFilter.includes(label)).length === 0 && (
          <MenuItem disabled>
            <ListItemText primary="No labels available" />
          </MenuItem>
        )}
      </Menu>

      {/* Import and Export */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", xl: "row" },
          gap: { xs: 0.3, sm: 0.4, md: 0.5, lg: 0.6, xl: 4 },
          padding: { xs: "2px", sm: 0.4, md: 0.5, lg: 0.75, xl: 2 },
          flexShrink: 0,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx, .csv"
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />
        <Tooltip
          title={
            !ContactPermissions.Add
              ? "You don't have permission to import contacts"
              : importProgress.isImporting
              ? "Import in progress..."
              : ""
          }
        >
          <span style={{ flex: 1 }}>
            <Button
              onClick={handleImportClick}
              variant="outlined"
              startIcon={!isXs ? (
                <Box
                  component={ExportIconBlack}
                  sx={{
                  width: { xs: "8px", sm: "10px", md: "12px", lg: "13px", xl: "18px" },
                  height: { xs: "8px", sm: "10px", md: "12px", lg: "13px", xl: "18px" },
                    color: (theme) => theme.palette.success.main,
                  }}
                />
              ) : null}
              disabled={!ContactPermissions.Add || importProgress.isImporting}
              sx={{
                width: "100%",
                maxWidth: "100%",
                textTransform: "none",
                fontSize: { xs: "8px", sm: "8px", md: "9px", lg: "10px", xl: "14px" },
                borderRadius: { xs: "3px", sm: "4px", md: "5px", lg: "6px", xl: "8px" },
                padding: { xs: "1px 2px", sm: "2px 5px", md: "3px 7px", lg: "4px 9px", xl: "8px 16px" },
                minHeight: { xs: "20px", sm: "22px", md: "26px", lg: "28px", xl: "36px" },
                whiteSpace: { xs: "nowrap", sm: "nowrap", md: "nowrap", lg: "nowrap", xl: "normal" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                boxSizing: "border-box",
                minWidth: 0,
                "& .MuiButton-startIcon": {
                  marginRight: { xs: "0px", sm: "2px", md: "3px", lg: "4px", xl: "8px" },
                  display: { xs: "none", sm: "flex" },
                  "& > *:nth-of-type(1)": {
                    fontSize: { xs: "8px", sm: "10px", md: "12px", lg: "13px", xl: "18px" },
                  },
                },
                "&.Mui-disabled": {
                  borderColor: "gray",
                  color: "gray",
                },
              }}
            >
              {importProgress.isImporting ? "Importing..." : "Import"}
            </Button>
          </span>
        </Tooltip>

        <Tooltip
          title={
            !ContactPermissions.Export
              ? "You don't have permission to export contacts"
              : ""
          }
        >
          <span style={{ flex: 1 }}>
            <Button
              variant="outlined"
              startIcon={!isXs ? (
                <Box
                  component={ImportIconBlack}
                  sx={{
                  width: { xs: "8px", sm: "10px", md: "12px", lg: "13px", xl: "18px" },
                  height: { xs: "8px", sm: "10px", md: "12px", lg: "13px", xl: "18px" },
                    color: (theme) => theme.palette.success.main,
                  }}
                />
              ) : null}
              onClick={handleExportClick}
              disabled={!ContactPermissions.Export}
              sx={{
                width: "100%",
                maxWidth: "100%",
                textTransform: "none",
                fontSize: { xs: "8px", sm: "8px", md: "9px", lg: "10px", xl: "14px" },
                borderRadius: { xs: "3px", sm: "4px", md: "5px", lg: "6px", xl: "8px" },
                padding: { xs: "1px 2px", sm: "2px 5px", md: "3px 7px", lg: "4px 9px", xl: "8px 16px" },
                minHeight: { xs: "20px", sm: "22px", md: "26px", lg: "28px", xl: "36px" },
                whiteSpace: { xs: "nowrap", sm: "nowrap", md: "nowrap", lg: "nowrap", xl: "normal" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                boxSizing: "border-box",
                minWidth: 0,
                "& .MuiButton-startIcon": {
                  marginRight: { xs: "0px", sm: "2px", md: "3px", lg: "4px", xl: "8px" },
                  display: { xs: "none", sm: "flex" },
                  "& > *:nth-of-type(1)": {
                    fontSize: { xs: "8px", sm: "10px", md: "12px", lg: "13px", xl: "18px" },
                  },
                },
                "&.Mui-disabled": {
                  borderColor: "gray",
                  color: "gray",
                },
              }}
            >
              Export
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: { xs: 0.3, sm: 0.4, md: 0.5, lg: 0.6, xl: 3 },
          padding: { xs: "2px", sm: 0.4, md: 0.5, lg: 0.75, xl: 2 },
          flex: 1,
          minHeight: 0,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          overflowY: { xs: "auto", sm: "auto", md: "auto", lg: "auto", xl: "auto" },
          overflowX: "hidden",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 0.3, sm: 0.4, md: 0.5, lg: 0.6, xl: 3 }, width: "100%", maxWidth: "100%", minWidth: 0 }}>
          {/* Add Label and Remove Label */}
          <Box sx={{ display: "flex", flexDirection: { xs: "column", xl: "row" }, gap: { xs: 0.3, sm: 0.4, md: 0.5, lg: 0.6, xl: 2 }, width: "100%", maxWidth: "100%", minWidth: 0 }}>
            <Button
              variant="outlined"
              startIcon={!isXs ? <LabelIcon sx={{ fontSize: { xs: "9px", sm: "10px", md: "12px", lg: "13px", xl: "20px" } }} /> : null}
              onClick={handleAddLabels}
              sx={{
                textTransform: "none",
                fontSize: { xs: "8px", sm: "8px", md: "9px", lg: "10px", xl: "14px" },
                borderRadius: { xs: "3px", sm: "4px", md: "5px", lg: "6px", xl: "8px" },
                padding: { xs: "1px 2px", sm: "2px 5px", md: "3px 7px", lg: "4px 9px", xl: "8px 16px" },
                minHeight: { xs: "20px", sm: "22px", md: "26px", lg: "28px", xl: "36px" },
                width: { xs: "100%", lg: "100%", xl: "auto" },
                maxWidth: "100%",
                minWidth: 0,
                flex: { xs: 1, lg: 1, xl: "none" },
                whiteSpace: { xs: "nowrap", sm: "nowrap", md: "nowrap", lg: "nowrap", xl: "normal" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                boxSizing: "border-box",
                "& .MuiButton-startIcon": {
                  marginRight: { xs: "0px", sm: "2px", md: "3px", lg: "3px", xl: "8px" },
                  display: { xs: "none", sm: "flex" },
                },
                "&.Mui-disabled": {
                  borderColor: "gray",
                  color: "gray",
                },
              }}
            >
              Add Label
            </Button>
            <Button
              variant="outlined"
              startIcon={!isXs ? <LabelOffIcon sx={{ fontSize: { xs: "9px", sm: "10px", md: "12px", lg: "13px", xl: "20px" } }} /> : null}
              onClick={handleRemoveLabels}
              sx={{
                textTransform: "none",
                fontSize: { xs: "8px", sm: "8px", md: "9px", lg: "10px", xl: "14px" },
                borderRadius: { xs: "3px", sm: "4px", md: "5px", lg: "6px", xl: "8px" },
                padding: { xs: "1px 2px", sm: "2px 5px", md: "3px 7px", lg: "4px 9px", xl: "8px 16px" },
                minHeight: { xs: "20px", sm: "22px", md: "26px", lg: "28px", xl: "36px" },
                width: { xs: "100%", lg: "100%", xl: "auto" },
                maxWidth: "100%",
                minWidth: 0,
                flex: { xs: 1, lg: 1, xl: "none" },
                whiteSpace: { xs: "nowrap", sm: "nowrap", md: "nowrap", lg: "nowrap", xl: "normal" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                boxSizing: "border-box",
                "& .MuiButton-startIcon": {
                  marginRight: { xs: "0px", sm: "2px", md: "3px", lg: "3px", xl: "8px" },
                  display: { xs: "none", sm: "flex" },
                },
                "&.Mui-disabled": {
                  borderColor: "gray",
                  color: "gray",
                },
              }}
            >
              Remove Label
            </Button>
          </Box>

          {/* Manage Labels - Full width */}
          <Box sx={{ display: "flex", gap: { xs: 0.3, sm: 0.4, md: 0.5, lg: 0.6, xl: 1 }, width: "100%", maxWidth: "100%", minWidth: 0 }}>
            <Button
              variant="outlined"
              startIcon={!isXs ? <SettingsIcon sx={{ fontSize: { xs: "9px", sm: "9px", md: "10px", lg: "11px", xl: "20px" } }} /> : null}
              onClick={handleManageLabels}
              sx={{
                textTransform: "none",
                fontSize: { xs: "8px", sm: "7px", md: "8px", lg: "9px", xl: "14px" },
                borderRadius: { xs: "3px", sm: "4px", md: "5px", lg: "6px", xl: "8px" },
                padding: { xs: "1px 2px", sm: "2px 5px", md: "3px 7px", lg: "4px 9px", xl: "8px 16px" },
                minHeight: { xs: "20px", sm: "22px", md: "26px", lg: "28px", xl: "36px" },
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                whiteSpace: { xs: "nowrap", sm: "nowrap", md: "nowrap", lg: "nowrap", xl: "normal" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                boxSizing: "border-box",
                "& .MuiButton-startIcon": {
                  marginRight: { xs: "0px", sm: "1px", md: "2px", lg: "2px", xl: "8px" },
                  display: { xs: "none", sm: "flex" },
                },
              }}
            >
              Manage Labels
            </Button>
          </Box>

          {/* Add to List */}
          

          {/* Group Management */}
          <Box sx={{ display: "flex", gap: { xs: 0.3, sm: 0.4, md: 0.5, lg: 0.6, xl: 1 }, width: "100%", maxWidth: "100%", minWidth: 0 }}>
            <Tooltip title="contact groups">
              <Button
                variant="outlined"
                startIcon={!isXs ? (
                  <Badge 
                    badgeContent={selectedContacts.length} 
                    color="primary"
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: { xs: "5px", sm: "6px", md: "7px", lg: "8px", xl: "12px" },
                        height: { xs: "9px", sm: "11px", md: "13px", lg: "15px", xl: "18px" },
                        minWidth: { xs: "9px", sm: "11px", md: "13px", lg: "15px", xl: "18px" },
                      },
                    }}
                  >
                    <GroupIcon sx={{ fontSize: { xs: "9px", sm: "10px", md: "12px", lg: "13px", xl: "20px" } }} />
                  </Badge>
                ) : null}
                onClick={handleGroupMenuClick}
                disabled={selectedContacts.length === 0}
                sx={{
                  textTransform: "none",
                  fontSize: { xs: "8px", sm: "8px", md: "9px", lg: "10px", xl: "14px" },
                  borderRadius: { xs: "3px", sm: "4px", md: "5px", lg: "6px", xl: "8px" },
                  padding: { xs: "1px 2px", sm: "2px 5px", md: "3px 7px", lg: "4px 9px", xl: "8px 16px" },
                  minHeight: { xs: "20px", sm: "22px", md: "26px", lg: "28px", xl: "36px" },
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  whiteSpace: { xs: "nowrap", sm: "nowrap", md: "nowrap", lg: "nowrap", xl: "normal" },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  boxSizing: "border-box",
                  "& .MuiButton-startIcon": {
                    marginRight: { xs: "0px", sm: "2px", md: "3px", lg: "3px", xl: "8px" },
                    display: { xs: "none", sm: "flex" },
                  },
                }}
              >
                Add to Group
              </Button>
            </Tooltip>
          </Box>

          {/* Delete */}
          <Box sx={{ display: "flex", gap: { xs: 0.3, sm: 0.4, md: 0.5, lg: 0.6, xl: 1 }, width: "100%", maxWidth: "100%", minWidth: 0 }}>
            <Tooltip
              title={
                !ContactPermissions.Delete
                  ? "You don't have permission to delete contacts"
                  : ""
              }
            >
              <span style={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
                <Button
                  variant="outlined"
                  startIcon={!isXs ? <DeleteIcon sx={{ fontSize: { xs: "9px", sm: "10px", md: "12px", lg: "13px", xl: "20px" } }} /> : null}
                  onClick={handleOpenDeleteContactsDialog}
                  disabled={!ContactPermissions.Delete}
                  sx={{
                    textTransform: "none",
                    fontSize: { xs: "8px", sm: "8px", md: "9px", lg: "10px", xl: "14px" },
                    borderRadius: { xs: "3px", sm: "4px", md: "5px", lg: "6px", xl: "8px" },
                    padding: { xs: "1px 2px", sm: "2px 5px", md: "3px 7px", lg: "4px 9px", xl: "8px 16px" },
                    minHeight: { xs: "20px", sm: "22px", md: "26px", lg: "28px", xl: "36px" },
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    whiteSpace: { xs: "nowrap", sm: "nowrap", md: "nowrap", lg: "nowrap", xl: "normal" },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    boxSizing: "border-box",
                    "& .MuiButton-startIcon": {
                      marginRight: { xs: "0px", sm: "2px", md: "3px", lg: "3px", xl: "8px" },
                      display: { xs: "none", sm: "flex" },
                    },
                    "&.Mui-disabled": {
                      borderColor: "gray",
                      color: "gray",
                    },
                  }}
                >
                  Delete
                </Button>
              </span>
            </Tooltip>
          </Box>

          {/* Create Contact - Only show in Contacts tab (activeTab === 0) */}
          {activeTab === 0 && (
            <Box sx={{ display: "flex", gap: { xs: 0.3, sm: 0.4, md: 0.5, lg: 0.6, xl: 1 }, width: "100%", maxWidth: "100%", minWidth: 0 }}>
              <Tooltip
                title={
                  !ContactPermissions.Add
                    ? "You don't have permission to create contacts"
                    : ""
                }
              >
                <span style={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
                  <Button
                    variant="contained"
                    startIcon={!isXs ? <AddIcon sx={{ fontSize: { xs: "9px", sm: "9px", md: "11px", lg: "12px", xl: "20px" } }} /> : null}
                    onClick={handleAddContact}
                    disabled={!ContactPermissions.Add}
                    sx={{
                      borderRadius: { xs: "3px", sm: "4px", md: "5px", lg: "6px", xl: "2rem" },
                      textTransform: "none",
                      width: "100%",
                      maxWidth: "100%",
                      minWidth: 0,
                      fontSize: { xs: "8px", sm: "7px", md: "8px", lg: "9px", xl: "14px" },
                      padding: { xs: "1px 2px", sm: "2px 5px", md: "3px 7px", lg: "4px 9px", xl: "8px 16px" },
                      minHeight: { xs: "20px", sm: "22px", md: "26px", lg: "28px", xl: "36px" },
                      whiteSpace: { xs: "nowrap", sm: "nowrap", md: "nowrap", lg: "nowrap", xl: "normal" },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      boxSizing: "border-box",
                      "& .MuiButton-startIcon": {
                        marginRight: { xs: "0px", sm: "1px", md: "2px", lg: "2px", xl: "8px" },
                        display: { xs: "none", sm: "flex" },
                      },
                      "&.Mui-disabled": {
                        backgroundColor: (theme) => theme.palette.action.disabledBackground,
                        color: (theme) => theme.palette.action.disabled,
                      },
                    }}
                  >
                    Create Contact
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%", maxWidth: 480 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Add Contact Modal */}
      <AddContact isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

      {/* Labels Dialog */}
      <LabelsDialog
        open={labelsDialogOpen}
        onClose={() => setLabelsDialogOpen(false)}
        contactIds={selectedContacts}
        isBulkOperation={true}
        onSuccess={handleLabelsDialogSuccess}
      />

      {/* Import Progress Dialog */}
      <Dialog
        open={showProgressDialog}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            backgroundColor: (theme) => theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <Typography variant="h6" component="div">
            Importing Contacts
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {importProgress.message}
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {Math.floor(importProgress.current)} of {importProgress.total} contacts processed
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold" }}>
                {importProgress.total > 0 ? Math.round((Math.floor(importProgress.current) / importProgress.total) * 100) : 0}%
              </Typography>
            </Box>
          </Box>
          <LinearProgress
            variant="determinate"
            value={importProgress.total > 0 ? (Math.floor(importProgress.current) / importProgress.total) * 100 : 0}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: (theme) => theme.palette.action.hover,
              "& .MuiLinearProgress-bar": {
                borderRadius: 4,
                backgroundColor: (theme) => 
                  importProgress.message.includes("failed") 
                    ? theme.palette.error.main 
                    : theme.palette.primary.main,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setShowProgressDialog(false);
              dispatch(resetImportProgress());
            }}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Group Management Menu */}
      <Menu
        anchorEl={groupMenuAnchorEl}
        open={Boolean(groupMenuAnchorEl)}
        onClose={handleGroupMenuClose}
        PaperProps={{
          sx: {
            minWidth: 250,
            borderRadius: "8px",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 2px 4px rgba(0,0,0,0.3)"
                : "0 2px 4px rgba(0,0,0,0.1)",
            backgroundColor: (theme) => theme.palette.background.paper,
          },
        }}
      >
        <MenuItem disabled>
          <ListItemText 
            primary={`${selectedContactsForGroup.length} contact${selectedContactsForGroup.length !== 1 ? 's' : ''} selected`}
            secondary="Select an action"
          />
        </MenuItem>
        <Divider />
        
        {groups.length > 0 ? [
          <MenuItem key="add-to-group-header" disabled>
            <ListItemText primary="Add to Group" />
          </MenuItem>,
          ...groups.map((group) => (
            <MenuItem
              key={group._id}
              onClick={() => handleAddToGroup(group._id)}
            >
              <ListItemIcon>
                <GroupIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary={group.name}
                secondary={`${group.contactCount || 0} contacts`}
              />
            </MenuItem>
          )),
          <Divider key="divider-1" />,
          <MenuItem key="remove-from-group-header" disabled>
            <ListItemText primary="Remove from Group" />
          </MenuItem>,
          ...groups.map((group) => (
            <MenuItem
              key={`remove-${group._id}`}
              onClick={() => handleRemoveFromGroup(group._id)}
            >
              <ListItemIcon>
                <GroupIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText 
                primary={group.name}
                secondary={`${group.contactCount || 0} contacts`}
              />
            </MenuItem>
          ))
        ] : (
          <MenuItem disabled>
            <ListItemText 
              primary="No groups available"
              secondary="Create contact groups first"
            />
          </MenuItem>
        )}
      </Menu>

      {/* Labels Dialog */}
      <LabelsDialog
        open={labelsDialogOpen}
        onClose={() => setLabelsDialogOpen(false)}
        contactIds={selectedContacts}
        isBulkOperation={true}
        onSuccess={handleLabelsDialogSuccess}
      />

      {/* Labels Manager */}
      <LabelsManager
        open={labelsManagerOpen}
        onClose={() => setLabelsManagerOpen(false)}
        onLabelDeleted={handleLabelsDialogSuccess}
      />

      {/* Export Format Selection Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={handleExportCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            backgroundColor: (theme) => theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <Typography variant="h6" component="div">
            Select Export Format
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
            Choose the format for exporting your contacts:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant={selectedFormat === 'excel' ? 'contained' : 'outlined'}
              onClick={() => setSelectedFormat('excel')}
              sx={{ 
                justifyContent: 'flex-start', 
                textTransform: 'none',
                py: 1.5,
                px: 2
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Excel (.xlsx)
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: selectedFormat === 'excel' ? 'white' : 'text.secondary'
                  }}
                >
                  Recommended for complex data with formatting
                </Typography>
              </Box>
            </Button>
            <Button
              variant={selectedFormat === 'csv' ? 'contained' : 'outlined'}
              onClick={() => setSelectedFormat('csv')}
              sx={{ 
                justifyContent: 'flex-start', 
                textTransform: 'none',
                py: 1.5,
                px: 2
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  CSV (.csv)
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: selectedFormat === 'csv' ? 'white' : 'text.secondary'
                  }}
                >
                  Compatible with most applications and spreadsheets
                </Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleExportCancel} color="secondary" variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleExportConfirm} 
            variant="contained" 
            color="primary"
            sx={{ textTransform: 'none' }}
          >
            Export {selectedFormat.toUpperCase()}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteContactsDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            backgroundColor: (theme) => theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <Typography variant="h6" component="div">
            Delete contacts
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography variant="body1" sx={{ textAlign: "center" }}>
            {bulkDeleteConfirmMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleDeleteContactsDialogClose}
            color="secondary"
            variant="outlined"
            disabled={deleteInProgress}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteSelectedContacts}
            variant="contained"
            color="error"
            disabled={deleteInProgress}
            sx={{ textTransform: "none" }}
          >
            {deleteInProgress ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Column Mapping Dialog */}
      <ColumnMappingDialog
        open={columnMappingOpen}
        onClose={() => {
          setColumnMappingOpen(false);
          setFileHeaders([]);
          setFileData([]);
        }}
        onConfirm={handleColumnMappingConfirm}
        headers={fileHeaders}
        previewData={fileData}
        extraContent={<ContactChannelPicker channelState={importChannel} />}
        confirmDisabled={!importChannel.ready}
      />
    </Box>
  );
};

export default ContactsAction;
